import React, { useState, useCallback } from "react";
import { Input, Button, Space, Typography } from "antd";
import { useIntl } from "react-intl";
import { getRatingDesc } from "./evaluationUtils";
import { SmileOutlined, CheckOutlined } from "@ant-design/icons";
import type { ThemeColors } from "./UCIndependentChat/hooks/useTheme";

const { TextArea } = Input;
const { Text, Title } = Typography;

// 评价等级配置
const RATING_CONFIG = {
  1: { text: "😠", color: "#ff4d4f", bgColor: "rgba(255, 77, 79, 0.1)" }, // red
  2: { text: "😔", color: "#ff7a45", bgColor: "rgba(255, 122, 69, 0.1)" }, // orange
  3: { text: "😐", color: "#ffc53d", bgColor: "rgba(255, 197, 61, 0.1)" }, // gold
  4: { text: "🙂", color: "#1890ff", bgColor: "rgba(24, 144, 255, 0.1)" }, // blue
  5: { text: "🥰", color: "#52c41a", bgColor: "rgba(82, 196, 26, 0.1)" }, // green
};

interface ChatEvaluationProps {
  onSubmit: (rating: number, feedback?: string) => void;
  onSkip: () => void;
  loading?: boolean;
  themeColors?: ThemeColors;
}

const ChatEvaluation: React.FC<ChatEvaluationProps> = ({
  onSubmit,
  onSkip,
  loading = false,
  themeColors,
}) => {
  const intl = useIntl();
  const [rating, setRating] = useState<number>(5);
  const [feedback, setFeedback] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 默认主题颜色（亮色模式）
  const defaultColors = {
    background: '#fff',
    evaluationBg: 'linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%)',
    text: '#262626',
    textSecondary: '#8c8c8c',
    border: '#d9d9d9',
    messageBg: '#fff',
    evaluationMeta: '#1890ff',
    evaluationText: '#52c41a',
    senderPlaceholder: '#9aa3b2',
  };
  
  // 使用传入的主题颜色或默认值
  const colors = themeColors || defaultColors;
  const isDark = colors.background === '#14172B';
  
  // 根据主题设置背景色
  // 暗色模式下使用纯色 #353847，亮色模式下使用渐变
  const backgroundColor = colors.evaluationBg === '#353847' 
    ? '#353847' 
    : colors.evaluationBg || defaultColors.evaluationBg;

  const handleSubmit = useCallback(async () => {
    if (!rating || rating === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(rating, feedback.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  }, [rating, feedback, onSubmit]);

  const handleSkip = useCallback(() => {
    onSkip();
  }, [onSkip]);

  const renderRatingDesc = (value: number) => getRatingDesc(intl, value);

  return (
    <div
      style={{
        margin: "16px 0",
        padding: "20px",
        background: backgroundColor,
        borderRadius: "12px",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <SmileOutlined
          style={{
            fontSize: "32px",
            color: colors.evaluationText || colors.evaluationMeta || "#52c41a",
            marginBottom: "8px",
            display: "block",
          }}
        />
        <Title level={4} style={{ margin: "0 0 8px 0", color: colors.text }}>
          {intl.formatMessage(
            { id: "evaluation.title" },
            { defaultMessage: "为本次对话评分" }
          )}
        </Title>
        <Text type="secondary" style={{ color: colors.textSecondary }}>
          {intl.formatMessage(
            { id: "evaluation.subtitle" },
            { defaultMessage: "您的反馈有助于我们改进服务质量" }
          )}
        </Text>
      </div>

      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          {([1, 2, 3, 4, 5] as const).map((level) => {
            const config = RATING_CONFIG[level];
            const isSelected = rating === level;

            return (
              <button
                key={level}
                onClick={() => !loading && !isSubmitting && setRating(level)}
                disabled={loading || isSubmitting}
                style={{
                  fontSize: "20px",
                  padding: "2px 4px",
                  border: `2px solid ${isSelected ? config.color : (colors.border || "#d9d9d9")}`,
                  borderRadius: "6px",
                  background: isSelected ? config.bgColor : (colors.messageBg || "#fff"),
                  cursor: loading || isSubmitting ? "not-allowed" : "pointer",
                  opacity: loading || isSubmitting ? 0.6 : 1,
                  transition: "all 0.2s",
                  transform: isSelected ? "scale(1.1)" : "scale(1)",
                }}
                onMouseEnter={(e) => {
                  if (!loading && !isSubmitting && !isSelected) {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.borderColor = config.color;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading && !isSubmitting && !isSelected) {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.borderColor = colors.border || "#d9d9d9";
                  }
                }}
              >
                {config.text}
              </button>
            );
          })}
        </div>
        {rating > 0 && (
          <div style={{ marginTop: "8px" }}>
            <Text style={{ color: colors.evaluationMeta || "#1890ff", fontWeight: 500 }}>
              {renderRatingDesc(rating)}
            </Text>
          </div>
        )}
      </div>

      <div style={{ marginBottom: "20px" }}>
        <TextArea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder={intl.formatMessage(
            {
              id: "evaluation.feedback.placeholder",
            },
            {
              defaultMessage: "请分享您的使用体验或建议（可选）",
            }
          )}
          rows={3}
          maxLength={500}
          disabled={loading || isSubmitting}
          style={{ 
            resize: "none",
            backgroundColor: colors.messageBg || (isDark ? "#1e2338" : "#fff"),
            color: colors.text || (isDark ? "#ffffff" : "#262626"),
            borderColor: colors.border || (isDark ? "#2a2f47" : "#d9d9d9"),
          }}
          styles={{
            textarea: {
              backgroundColor: colors.messageBg || (isDark ? "#1e2338" : "#fff"),
              color: colors.text || (isDark ? "#ffffff" : "#262626"),
            },
          }}
          classNames={{
            textarea: isDark ? "dark-textarea" : "",
          }}
          showCount
        />
      </div>

      <Space style={{ width: "100%", justifyContent: "center" }} size="middle">
        <Button
          onClick={handleSkip}
          disabled={loading || isSubmitting}
          style={{ 
            minWidth: "80px",
            backgroundColor: isDark ? colors.messageBg || "#1e2338" : undefined,
            color: isDark ? colors.text || "#ffffff" : undefined,
            borderColor: isDark ? colors.border || "#2a2f47" : undefined,
          }}
        >
          {intl.formatMessage(
            { id: "evaluation.skip" },
            { defaultMessage: "跳过" }
          )}
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={!rating || rating === 0 || loading || isSubmitting}
          loading={isSubmitting}
          icon={<CheckOutlined />}
          style={{ minWidth: "80px" }}
        >
          {intl.formatMessage(
            { id: "evaluation.submit" },
            { defaultMessage: "提交评价" }
          )}
        </Button>
      </Space>
    </div>
  );
};

export default ChatEvaluation;
